import * as React from 'react';
import { BodyPrimary, BodySecondary, H2, LabelMini } from '@liveauctioneers/hammer-ui-core/text';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import {
    fetchBankAccountsIfNeeded,
    removeBankAccount,
} from '@/redux/modules/account/payments/bankAccount/bankAccount.actions';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import {
    getBankAccounts,
    getBankAccountsLoaded,
    getDefaultBankAccount,
} from '@/redux/modules/account/payments/bankAccount/bankAccount.selectors';
import {
    getBidderMobileVerified,
    getBidderPhoneNumber,
    getUserData,
} from '@/redux/modules/account/user/user.selectors';
import { getProfileAddress } from '@/redux/modules/userAddress';
import { getUiUpdateAccount } from '@/redux/modules/account/updateAccount/updateAccount';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { openChangePasswordModal, openDeleteAccountConfirmationModal } from '@/redux/modules/modal';
import { PaymentProviders } from '@/enums';
import { preventDefault } from '@/utils/preventDefault';
import { updateAccountDetails } from '@/redux/modules/account/updateAccount/updateAccount.actions';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import ApprovePhoneNumber from '@/components/ApprovePhoneNumber/ApprovePhoneNumber';
import BankAccountOnFileWrapper from './components/BankAccountOnFileWrapper';
import BidderAddressList from '@/components/BidderAddressList/BidderAddressList';
import CardManagement from './components/CardManagement';
import Input from '@liveauctioneers/caterwaul-components/lib/Input/Input';
import isEqual from 'react-fast-compare';
import ProfileBasicsForm, { ProfileBasicsData } from '@/components/ProfileBasicsForm/ProfileBasicsForm';
import ResellerCheckbox from '@/components/ResellerCheckbox/ResellerCheckbox';
import SecurityForm from '@/pages/AccountSettings/components/SecurityForm/SecurityForm';
import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';
import styled from 'styled-components';
import usePrevious from '@liveauctioneers/caterwaul-components/lib/hooks/usePrevious';

const ProfileForm = () => {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const { addresses, email, username } = useAppSelector(getUserData);
    const { submitted: editProfileSubmitted } = useAppSelector(getUiUpdateAccount);
    const profileAddress = useAppSelector(getProfileAddress);
    const bidderPhoneNumber = useAppSelector(getBidderPhoneNumber);
    const profileBasicsInit: ProfileBasicsData = {
        email,
        firstName: profileAddress.firstName,
        lastName: profileAddress.lastName,
        mobileNumber: bidderPhoneNumber,
    };
    const [profileBasicsForm, setProfileBasicsForm] = React.useState<ProfileBasicsData>(profileBasicsInit);
    const prevProfileAddress = usePrevious(profileAddress);
    const [profileBasicsFormValid, setProfileBasicsFormValid] = React.useState(true);
    const [profileBasicsFormSubmittable, setProfileBasicsFormSubmittable] = React.useState(false);

    const mobileVerified = useAppSelector(getBidderMobileVerified);
    const defaultBankAccount = useAppSelector((state) => getDefaultBankAccount(state, PaymentProviders.Payrix));
    const bankAccounts = useAppSelector(getBankAccounts);
    const bankAccountsLoaded = useAppSelector(getBankAccountsLoaded);

    const bankAccountToDisplay = defaultBankAccount || bankAccounts[bankAccounts.length - 1];

    const onBankAccountDelete = (bankAccountId: number, plaidAccountId?: string) => {
        dispatch(removeBankAccount({ bankAccountId, plaidAccountId }));
    };

    const onProfileBasicsChange = (valid: boolean, form: ProfileBasicsData) => {
        if (!isEqual(form, profileBasicsForm)) {
            setProfileBasicsForm(form);
            setProfileBasicsFormValid(valid);
            setProfileBasicsFormSubmittable(valid);
        }
    };

    const submitEditProfile = () => {
        const { email, firstName, lastName, mobileNumber } = profileBasicsForm;

        if (!profileBasicsFormValid) {
            return;
        }

        dispatch(
            updateAccountDetails({
                email,
                firstName,
                lastName,
                mobile: mobileNumber.replace(/[^\d]+/g, ''),
                zipcode: profileAddress.zipcode,
            })
        );

        setProfileBasicsFormSubmittable(false);
    };

    React.useEffect(() => {
        if (!isEqual(profileAddress, prevProfileAddress)) {
            setProfileBasicsForm({ ...profileBasicsForm, ...profileAddress });
        }
    }, [prevProfileAddress, profileAddress, profileBasicsForm]);

    React.useEffect(() => {
        if (!Boolean(bankAccountsLoaded)) {
            dispatch(fetchBankAccountsIfNeeded());
        }
    }, [bankAccountsLoaded, dispatch]);

    const openChangeModal = React.useCallback(() => dispatch(openChangePasswordModal()), [dispatch]);

    const openDeleteAccountModal = React.useCallback(() => dispatch(openDeleteAccountConfirmationModal()), [dispatch]);

    return (
        <StyledProfileFormContainer>
            <StyledProfileText data-testid="myProfileTitle">
                <FormattedMessage id="my_profile" />
            </StyledProfileText>
            <StyledForm
                data-testid="profileForm"
                onSubmit={preventDefault}
            >
                <StyledFormBasicsContainer>
                    <StyledProfileBasicsForm
                        formData={profileBasicsForm}
                        mobileVerified={mobileVerified}
                        updateParentForm={onProfileBasicsChange}
                    />
                    {!mobileVerified && (
                        <StyledApprovePhoneContainer>
                            <ApprovePhoneNumber />
                        </StyledApprovePhoneContainer>
                    )}
                </StyledFormBasicsContainer>
                <PasswordDiv>
                    <StyledPasswordInput
                        aria-label={formatMessage({ id: 'password' })}
                        autoComplete="off"
                        disabled
                        id="password"
                        label={formatMessage({ id: 'password' })}
                        testid="accountSettingsPassword"
                        type="password"
                        valid
                        value="**********"
                    />
                    <StyledInlineButton
                        data-testid="changePasswordLink"
                        onClick={openChangeModal}
                        size="sm"
                    >
                        {formatMessage({ id: 'change_password' })}
                    </StyledInlineButton>
                </PasswordDiv>
                <StyledResellerCheckbox />
                <StyledUsernameText>
                    <FormattedMessage id="accountSettings.username" />
                </StyledUsernameText>
                <StyledUserNameContainer>
                    <BodySecondary>{username}</BodySecondary>
                </StyledUserNameContainer>

                <div>
                    <ShouldLogInContainer runFunction={submitEditProfile}>
                        <StyledSaveButton
                            data-testid="myProfileSaveButton"
                            disabled={editProfileSubmitted || !profileBasicsFormValid || !profileBasicsFormSubmittable}
                            loading={editProfileSubmitted}
                        >
                            {formatMessage({ id: 'addresses.save' })}
                        </StyledSaveButton>
                    </ShouldLogInContainer>
                </div>
                <StyledSecurityForm />
                {/* This id is used to navigate users from the email url, directly to the payment information */}
                <StyledCardManagement id="card-management">
                    <CardManagement />
                </StyledCardManagement>
                <StyledBankAccountText>
                    <FormattedMessage id="accountSettings.bankAccountOnFileLabel" />
                </StyledBankAccountText>
                <BankAccountOnFileWrapper
                    bankAccountToDisplay={bankAccountToDisplay}
                    onBankAccountDelete={onBankAccountDelete}
                />
                <StyledSpacer />
                <TitleText>
                    <FormattedMessage id="address" />
                </TitleText>
                <BidderAddressList
                    addresses={addresses}
                    canEdit
                    setDefaultOnSelect
                    showRemoveButton
                    showSetDefaultButton
                    showTitle
                />
                <StyledSpacer />
                <TitleText>
                    <FormattedMessage id="accountSettings.deleteAccount" />
                </TitleText>
                <BodyPrimary>
                    <FormattedMessage id="accountSettings.deleteAccountWarning" />
                </BodyPrimary>
                <Button
                    className="mt-6 w-full mdMin:max-w-min"
                    data-testid="deleteAccountLink"
                    onClick={openDeleteAccountModal}
                    style="critical"
                    variant="outlined"
                >
                    {formatMessage({ id: 'accountSettings.delete' })}
                </Button>
            </StyledForm>
        </StyledProfileFormContainer>
    );
};

export default ProfileForm;

const StyledProfileFormContainer = styled.div`
    width: 100%;
    margin-bottom: 128px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-bottom: 0;
    }
`;

const StyledProfileText = styled(H2)`
    margin-bottom: 32px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletWidth}) {
        margin-bottom: 16px;
    }
`;

const StyledForm = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-content: stretch;
    align-items: stretch;
    margin: 0;
    padding: 0;
    font-family: ${({ theme }) => theme.typography.fontPrimary};
    color: ${({ theme }) => theme.colors.grey100};
    line-height: 1;

    > p {
        margin: 0;
    }
`;

const StyledFormBasicsContainer = styled.div`
    display: flex;
    flex-direction: row;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletWidth}) {
        flex-direction: column-reverse;
    }
`;

const StyledProfileBasicsForm = styled(ProfileBasicsForm)`
    width: 583px;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletWidth}) {
        width: 100%;
    }
`;

// this container is needed to shift the content upward without affecting
// inner vertical flex styling; supports desktop/mobile layout shifting
const StyledApprovePhoneContainer = styled.div`
    margin-top: -64px;
    margin-left: 24px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletWidth}) {
        margin: 16px 0 24px;
    }
`;

const PasswordDiv = styled.div`
    margin-top: 8px;
    display: flex;
    flex: row;
    align-items: center;
`;

const StyledPasswordInput = styled(Input).attrs(() => ({
    inputClassName: 'inputClassName',
}))`
    max-width: 583px;
    width: 100%;
    margin-right: 24px;
    .inputClassName {
        color: ${({ theme }) => theme.colors.grey300} !important;
    }
`;

const StyledInlineButton = styled(InlineButton)`
    margin-top: -14px;
`;

const StyledSecurityForm = styled(SecurityForm)`
    margin-top: 64px;
`;

const StyledCardManagement = styled.div`
    margin-top: 64px;
`;

const StyledResellerCheckbox = styled(ResellerCheckbox)`
    margin-bottom: 32px;
    display: block;
`;

const StyledUsernameText = styled(LabelMini)`
    margin-bottom: 4px;
`;

const StyledUserNameContainer = styled.div`
    margin-bottom: 32px;
`;

const StyledSpacer = styled.div`
    height: 1px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey400};
    margin: 32px 0 48px 0;
    width: 100%;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletWidth}) {
        margin-bottom: 64px;
    }
`;

const StyledBankAccountText = styled(LabelMini)`
    margin-bottom: 10px;
    margin-top: 10px;
`;

const StyledSaveButton = styled(Button)`
    margin-top: 24px;
    width: 194px;
`;

const TitleText = styled(H2)`
    margin-bottom: 16px;
`;
